import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Link } from 'react-router-dom'
import { ExternalLink, Loader2 } from 'lucide-react'

interface SubscriptionData {
  plan: string
  stripe_status: string | null
  current_period_end: number | null
  usage_this_month: {
    pre_reads: number
    pre_reads_limit: number
    prompt_tokens: number
    completion_tokens: number
  }
}

const PLAN_LABELS: Record<string, { name: string; badge: string; color: string }> = {
  sandbox:        { name: 'Sandbox',        badge: 'Free',         color: 'text-slate-500'  },
  researcher:     { name: 'Researcher',     badge: 'Researcher',   color: 'text-echo-indigo' },
  agency_pro:     { name: 'Agency Pro',     badge: 'Agency Pro',   color: 'text-echo-indigo' },
  agency_studio:  { name: 'Agency Studio+', badge: 'Studio+',      color: 'text-echo-indigo' },
  enterprise:     { name: 'Enterprise',     badge: 'Enterprise',   color: 'text-echo-indigo' },
}

export function BillingPage() {
  const [sub, setSub] = useState<SubscriptionData | null>(null)
  const [upgrading, setUpgrading] = useState(false)
  const [openingPortal, setOpeningPortal] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('echo_session')
    if (!token) return
    fetch('/api/billing/subscription', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then((data: unknown) => { if (data) setSub(data as SubscriptionData) })
      .catch(() => {})
  }, [])

  async function handleUpgrade(plan: string) {
    setUpgrading(true)
    try {
      const token = localStorage.getItem('echo_session')
      const res = await fetch('/api/billing/checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
        body: JSON.stringify({ plan }),
      })
      const data = await res.json() as { checkout_url?: string; message?: string }
      if (data.checkout_url) {
        window.location.href = data.checkout_url
      } else {
        alert(data.message ?? 'Stripe not configured yet.')
      }
    } finally {
      setUpgrading(false)
    }
  }

  async function handlePortal() {
    setOpeningPortal(true)
    try {
      const token = localStorage.getItem('echo_session')
      const res = await fetch('/api/billing/portal-session', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token ?? ''}` },
      })
      const data = await res.json() as { portal_url?: string; message?: string; error?: string }
      if (data.portal_url) {
        window.open(data.portal_url, '_blank')
      } else {
        alert(data.message ?? data.error ?? 'Could not open portal.')
      }
    } finally {
      setOpeningPortal(false)
    }
  }

  const planInfo = PLAN_LABELS[sub?.plan ?? 'sandbox']
  const usedPct = sub
    ? Math.min(100, Math.round((sub.usage_this_month.pre_reads / sub.usage_this_month.pre_reads_limit) * 100))
    : 0
  const isActive = sub?.stripe_status === 'active'
  const isPaid = sub?.plan && sub.plan !== 'sandbox'

  return (
    <div className="p-6 space-y-8 max-w-3xl pb-20 md:pb-6">
      <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Billing</h1>

      {/* Current plan */}
      <Card className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-deep-ink">Current plan</p>
            <p className={`text-xs mt-0.5 ${planInfo.color}`}>{planInfo.name}</p>
          </div>
          <Badge variant={isPaid ? 'success' : 'demo'}>{planInfo.badge}</Badge>
        </div>

        {sub?.stripe_status && (
          <p className="text-xs text-slate-500">
            Subscription status: <span className="font-medium">{sub.stripe_status}</span>
            {sub.current_period_end && ` · renews ${new Date(sub.current_period_end).toLocaleDateString()}`}
          </p>
        )}

        {isPaid ? (
          <div className="flex gap-3">
            <Button variant="secondary" size="sm" onClick={handlePortal} disabled={openingPortal}>
              {openingPortal ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <ExternalLink size={14} className="mr-1.5" />}
              Manage subscription
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">You're on the free Sandbox plan. Upgrade to run real pre-reads and unlock all features.</p>
            <div className="flex gap-3">
              <Button variant="primary" size="sm" onClick={() => handleUpgrade('researcher')} disabled={upgrading}>
                {upgrading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                Upgrade to Researcher
              </Button>
              <Link to="/pricing">
                <Button variant="secondary" size="sm">View all plans</Button>
              </Link>
            </div>
          </div>
        )}
      </Card>

      {/* Usage */}
      <Card className="space-y-4">
        <h2 className="text-sm font-medium text-deep-ink">Usage this month</h2>

        {/* Pre-reads bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Pre-reads run</span>
            <span className="text-slate-500 text-xs">
              {sub?.usage_this_month.pre_reads ?? 0} / {sub?.usage_this_month.pre_reads_limit === 99999 ? '∞' : sub?.usage_this_month.pre_reads_limit ?? 10}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-border overflow-hidden">
            <div
              className={`h-full rounded-full ${usedPct >= 90 ? 'bg-red-500' : usedPct >= 70 ? 'bg-orange-400' : 'bg-echo-indigo'}`}
              style={{ width: `${usedPct}%` }}
            />
          </div>
        </div>

        {[
          { label: 'Prompt tokens', value: sub?.usage_this_month.prompt_tokens.toLocaleString() ?? '0' },
          { label: 'Completion tokens', value: sub?.usage_this_month.completion_tokens.toLocaleString() ?? '0' },
        ].map(item => (
          <div key={item.label} className="flex items-center justify-between text-sm py-2 border-b border-border-gray last:border-0">
            <span className="text-slate-600">{item.label}</span>
            <span className="text-slate-400 text-xs font-mono">{item.value}</span>
          </div>
        ))}
      </Card>

      {/* Invoice billing (Agency Studio+) */}
      {(sub?.plan === 'agency_studio' || sub?.plan === 'enterprise') && (
        <InvoiceBillingSection />
      )}

      {/* Portal note */}
      <div className="bg-soft-panel rounded-xl p-5 border border-border-gray">
        <p className="text-sm text-slate-600">
          {isActive
            ? 'Invoices, payment methods, and cancellation are managed in the Stripe Customer Portal.'
            : 'After upgrading, all billing is managed via Stripe. You can cancel or change your plan at any time.'}
        </p>
      </div>
    </div>
  )
}

function InvoiceBillingSection() {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [billingName, setBillingName] = useState('')
  const [billingEmail, setBillingEmail] = useState('')
  const [netTerms, setNetTerms] = useState(30)
  const [poRequired, setPoRequired] = useState(false)
  const [saved, setSaved] = useState(false)

  const DEMO_INVOICES = [
    { invoice_id: 'inv_001', amount_cents: 5000000, status: 'paid', issued_at: Date.now() - 90 * 24 * 60 * 60 * 1000, due_at: Date.now() - 60 * 24 * 60 * 60 * 1000, po_number: 'PO-2026-001' },
    { invoice_id: 'inv_002', amount_cents: 5000000, status: 'open', issued_at: Date.now() - 5 * 24 * 60 * 60 * 1000, due_at: Date.now() + 25 * 24 * 60 * 60 * 1000, po_number: null },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium text-deep-ink">Invoice Billing</h2>
        <Button variant="ghost" size="sm" onClick={() => setMode(mode === 'edit' ? 'view' : 'edit')}>
          {mode === 'edit' ? 'Cancel' : 'Edit billing details'}
        </Button>
      </div>

      {mode === 'edit' ? (
        <Card className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Company name</label>
              <input className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-human-teal/30" value={billingName} onChange={e => setBillingName(e.target.value)} placeholder="Acme Research Agency" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Billing email</label>
              <input className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-human-teal/30" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} placeholder="billing@example.com" type="email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Net terms</label>
              <select className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-human-teal/30" value={netTerms} onChange={e => setNetTerms(parseInt(e.target.value))}>
                <option value={0}>Due on receipt</option>
                <option value={15}>NET-15</option>
                <option value={30}>NET-30</option>
                <option value={60}>NET-60</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" id="po-req" checked={poRequired} onChange={e => setPoRequired(e.target.checked)} className="rounded" />
              <label htmlFor="po-req" className="text-sm text-slate-600">Require PO number on invoices</label>
            </div>
          </div>
          <Button variant="primary" size="sm" onClick={() => { setSaved(true); setMode('view'); setTimeout(() => setSaved(false), 2000) }}>
            {saved ? 'Saved!' : 'Save billing details'}
          </Button>
        </Card>
      ) : null}

      <Card>
        <h3 className="text-sm font-medium text-deep-ink mb-3">Invoice history</h3>
        <div className="space-y-2">
          {DEMO_INVOICES.map(inv => (
            <div key={inv.invoice_id} className="flex items-center justify-between py-2 border-b border-border-gray last:border-0">
              <div>
                <p className="text-sm text-deep-ink">${(inv.amount_cents / 100).toLocaleString()}</p>
                <p className="text-xs text-slate-400">
                  Issued {new Date(inv.issued_at).toLocaleDateString()} · Due {new Date(inv.due_at).toLocaleDateString()}
                  {inv.po_number ? ` · PO: ${inv.po_number}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={inv.status === 'paid' ? 'success' : 'warning'} size="sm">{inv.status}</Badge>
                <Button variant="ghost" size="sm">PDF</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
